import { getMe } from '@groundly/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { call, isUnauthenticated } from '../api/call.js';
import { verifyEmail } from '../auth/auth-client.js';
import { DangerAlert } from '../components/DangerAlert.js';
import { PageHeading } from '../components/PageHeading.js';
import { CheckingSession } from '../components/SessionStates.js';
import { PreAccountCard, PreAccountFrame } from '../frame/PreAccountFrame.js';

/**
 * The page the link in the verification email opens (account sign-up `ux.md`,
 * flow C; `flow.md`, E). Loading it verifies nothing by itself: the dashboard
 * sends the token to Better Auth, then asks who is signed in, because Better
 * Auth answers "verified you" and "already verified, nobody signed in" alike.
 *
 * It processes the link whatever session the browser holds: whoever has the
 * link controls the address (account sign-up `technical.md`). Once the token
 * is used or refused, the link is replaced in the history so it isn't kept;
 * when Groundly couldn't be reached it stays, because it may still work.
 */
const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute('/verify-email/')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: VerifyEmailPage,
});

/** How long the check runs before "Verifying your email address" shows. */
const SHOW_VERIFYING_AFTER_MS = 300;

type PageState = 'working' | 'verifying' | 'failed';

function VerifyEmailPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<PageState>('working');
  // Once per page: a second request for the same link would race the first.
  const started = useRef(false);

  useEffect(() => {
    document.title = 'Verifying your email address · Groundly';
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const showVerifying = window.setTimeout(() => {
      setState((current) => (current === 'working' ? 'verifying' : current));
    }, SHOW_VERIFYING_AFTER_MS);

    const goToResend = (link: 'expired' | 'invalid') =>
      navigate({ to: '/verify-email/resend', search: { link }, replace: true });

    void (async () => {
      const outcome = token === undefined ? { kind: 'invalid' as const } : await verifyEmail(token);

      if (outcome.kind === 'expired' || outcome.kind === 'invalid') {
        window.clearTimeout(showVerifying);
        await goToResend(outcome.kind);
        return;
      }
      if (outcome.kind === 'failed') {
        window.clearTimeout(showVerifying);
        setState('failed');
        return;
      }

      // Better Auth answered 200: signed in now, or the link was already used
      // and nobody is signed in here (`flow.md`, E4).
      try {
        await call(getMe);
      } catch (error) {
        window.clearTimeout(showVerifying);
        if (isUnauthenticated(error)) {
          await goToResend('invalid');
        } else {
          setState('failed');
        }
        return;
      }

      window.clearTimeout(showVerifying);
      // No page may show a previous Account User's data
      // (`patterns/frontend-conventions.md`, 1). `/` asks where they land.
      queryClient.clear();
      await navigate({ href: '/', replace: true });
    })();

    // No cleanup: the work runs once per page, and React's development
    // double-run of effects must not cancel the "Verifying" timer it started.
  }, [navigate, queryClient, token]);

  if (state === 'working') return <CheckingSession />;

  return (
    <PreAccountFrame>
      {state === 'verifying' ? (
        <div aria-busy="true">
          <PreAccountCard head={<PageHeading>Verifying your email address</PageHeading>}>
            <p className="visually-hidden" role="status">
              Verifying your email address
            </p>
          </PreAccountCard>
        </div>
      ) : (
        <PreAccountCard head={<PageHeading>Verifying your email address</PageHeading>}>
          <DangerAlert title="Couldn't verify your email address">
            Groundly couldn&apos;t be reached. Open the link again in a moment.
          </DangerAlert>
        </PreAccountCard>
      )}
    </PreAccountFrame>
  );
}
